import React, { useState, useEffect } from 'react';
import { X, FolderOpen, Folder, Target, CheckCircle, Clock, Calendar, AlertCircle, BarChart, TrendingUp, Plus } from 'lucide-react';
import { Project, Matter, Goal, Task, db, GoalTemplate } from '../../lib/db';
import { format } from 'date-fns';
import { TemplateModal } from '../templates/TemplateModal';
import { GoalTemplateEditModal } from '../goals/GoalTemplateEditModal';

interface ProjectDetailModalProps {
  project: Project;
  matters: Matter[];
  goals: Goal[];
  tasks?: Task[];
  isOpen: boolean;
  onClose: () => void;
  onEdit: (project: Project) => void;
  userId: string;
  onGoalsUpdate?: () => void;
}

export function ProjectDetailModal({
  project,
  matters,
  goals,
  tasks,
  isOpen,
  onClose,
  onEdit,
  userId,
  onGoalsUpdate,
}: ProjectDetailModalProps) {
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState<GoalTemplate[]>([]);
  const [selectedYear] = useState(new Date().getFullYear());
  const [editingTemplate, setEditingTemplate] = useState<{
    template: GoalTemplate;
    matterId?: string;
    projectId?: string;
  } | null>(null);

  useEffect(() => {
    if (userId && showTemplateModal) {
      loadTemplates();
    }
  }, [userId, showTemplateModal]);

  const loadTemplates = async () => {
    try {
      const allTemplates = await db.goalTemplates.findAll(userId);
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleCreateFromTemplate = () => {
    setShowTemplateModal(true);
  };

  const handleTemplateSelect = async (template: GoalTemplate, matterId?: string, projectId?: string) => {
    // Show the edit modal with the project already selected
    setEditingTemplate({ template, matterId, projectId: project.id });
    setShowTemplateModal(false);
  };

  const handleTemplateConfirm = async (data: {
    title: string;
    description?: string;
    category?: string;
    priority: number;
    start_date?: string;
    deadline?: string;
    matter_id?: string;
    project_id?: string;
  }) => {
    if (!editingTemplate) return;

    try {
      // Create the goal from template with edited data
      const newGoal = await db.goals.create({
        user_id: userId,
        title: data.title,
        description: data.description,
        category: data.category,
        priority: data.priority,
        year: selectedYear,
        position: goals.length,
        matter_id: data.matter_id || undefined,
        project_id: data.project_id || undefined,
        start_date: data.start_date ? new Date(data.start_date) : undefined,
        deadline: data.deadline ? new Date(data.deadline) : undefined,
      } as Omit<Goal, 'id' | 'created_at' | 'updated_at'>);
      
      // Create tasks from templates with dependencies
      await db.createTasksFromTemplateWithDependencies(newGoal.id, editingTemplate.template.id, userId);
      
      setEditingTemplate(null);
      onGoalsUpdate?.();
    } catch (error) {
      console.error('Error creating goal from template:', error);
      alert('Failed to create deliverable from template');
    }
  };

  if (!isOpen) return null;

  // Get associated data
  const associatedMatters = matters.filter(matter => matter.project_id === project.id);
  const directGoals = goals.filter(goal => goal.project_id === project.id);
  const mattersGoals = goals.filter(goal => 
    goal.matter_id && associatedMatters.some(matter => matter.id === goal.matter_id)
  );
  const allProjectGoals = [...directGoals, ...mattersGoals];
  const uniqueGoals = Array.from(new Map(allProjectGoals.map(g => [g.id, g])).values());
  
  const goalIds = uniqueGoals.map(g => g.id);
  const projectTasks = tasks?.filter(task => task.goal_id && goalIds.includes(task.goal_id)) || [];
  
  // Calculate statistics
  const completedMatters = associatedMatters.filter(m => m.status === 'Completed');
  const activeMatters = associatedMatters.filter(m => m.status === 'Active');
  
  const completedGoals = uniqueGoals.filter(goal => {
    const goalTasks = projectTasks.filter(t => t.goal_id === goal.id);
    return goalTasks.length > 0 && goalTasks.every(t => t.status === 'Complete');
  });
  
  const completedTasks = projectTasks.filter(task => task.status === 'Complete');
  const inProgressTasks = projectTasks.filter(task => task.status === 'In Progress');
  const completionRate = projectTasks.length > 0 
    ? Math.round((completedTasks.length / projectTasks.length) * 100) 
    : 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Archived':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return 'Not set';
    return format(new Date(date), 'MMM d, yyyy');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FolderOpen className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{project.title}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Project Overview
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Status and Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Start Date</h3>
                <div className="flex items-center text-gray-900">
                  <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                  {formatDate(project.start_date)}
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Deadline</h3>
                <div className="flex items-center text-gray-900">
                  <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                  {formatDate(project.deadline)}
                </div>
              </div>
            </div>

            {/* Description */}
            {project.description && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{project.description}</p>
              </div>
            )}

            {/* High-level Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <Folder className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{associatedMatters.length}</div>
                <div className="text-sm text-gray-600">Matters</div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <Target className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{uniqueGoals.length}</div>
                <div className="text-sm text-gray-600">Deliverables</div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{completedGoals.length}</div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
              
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <TrendingUp className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{projectTasks.length}</div>
                <div className="text-sm text-gray-600">Total Tasks</div>
              </div>
              
              <div className="bg-indigo-50 rounded-lg p-4 text-center">
                <BarChart className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{completionRate}%</div>
                <div className="text-sm text-gray-600">Complete</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Overall Project Progress</span>
                <span>{completedTasks.length} of {projectTasks.length} tasks complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 h-4 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{ width: `${completionRate}%` }}
                >
                  {completionRate > 10 && (
                    <span className="text-xs text-white font-medium">{completionRate}%</span>
                  )}
                </div>
              </div>
            </div>

            {/* Matters Section */}
            {associatedMatters.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Matters in this Project</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {associatedMatters.map(matter => {
                    const matterGoals = goals.filter(g => g.matter_id === matter.id);
                    const matterGoalIds = matterGoals.map(g => g.id);
                    const matterTasks = projectTasks.filter(t => t.goal_id && matterGoalIds.includes(t.goal_id));
                    const completedMatterTasks = matterTasks.filter(t => t.status === 'Complete');
                    const matterProgress = matterTasks.length > 0 
                      ? Math.round((completedMatterTasks.length / matterTasks.length) * 100)
                      : 0;
                    
                    return (
                      <div key={matter.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <Folder className="h-5 w-5 text-purple-600" />
                            <h4 className="font-medium text-gray-900">{matter.title}</h4>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            matter.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            matter.status === 'Active' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {matter.status}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              {matterGoals.length} deliverables • {completedMatterTasks.length}/{matterTasks.length} tasks
                            </span>
                            <span className="font-medium">{matterProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full"
                              style={{ width: `${matterProgress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Direct Deliverables */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Direct Deliverables (not in any matter)</h3>
                <button
                  onClick={handleCreateFromTemplate}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  From Template
                </button>
              </div>
              {directGoals.length > 0 ? (
                <div className="space-y-3">
                  {directGoals.map(goal => {
                    const goalTasks = projectTasks.filter(t => t.goal_id === goal.id);
                    const completedGoalTasks = goalTasks.filter(t => t.status === 'Complete');
                    const goalProgress = goalTasks.length > 0 
                      ? Math.round((completedGoalTasks.length / goalTasks.length) * 100)
                      : 0;
                    
                    return (
                      <div key={goal.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <Target className="h-4 w-4 text-blue-600" />
                              <h4 className="font-medium text-gray-900">{goal.title}</h4>
                            </div>
                            <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                              {goal.category && <span>Category: {goal.category}</span>}
                              <span>{completedGoalTasks.length}/{goalTasks.length} tasks</span>
                              {goal.deadline && (
                                <span>Due: {format(new Date(goal.deadline), 'MMM d')}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">{goalProgress}%</div>
                            <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                              <div 
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${goalProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No direct deliverables yet. Click "From Template" to add one.</p>
              )}
            </div>

            {/* Task Distribution */}
            {projectTasks.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Task Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Total Tasks</div>
                    <div className="text-2xl font-bold text-gray-900">{projectTasks.length}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Completed</div>
                    <div className="text-2xl font-bold text-green-600">{completedTasks.length}</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">In Progress</div>
                    <div className="text-2xl font-bold text-yellow-600">{inProgressTasks.length}</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Not Started</div>
                    <div className="text-2xl font-bold text-red-600">
                      {projectTasks.length - completedTasks.length - inProgressTasks.length}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  onEdit(project);
                  onClose();
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Edit Project
              </button>
              <button
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Template Modal */}
      {showTemplateModal && (
        <TemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onSelectTemplate={handleTemplateSelect}
          userId={userId}
          matters={matters}
          projects={[project]}
        />
      )}
      
      {/* Goal Template Edit Modal */}
      {editingTemplate && (
        <GoalTemplateEditModal
          template={editingTemplate.template}
          isOpen={!!editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onConfirm={handleTemplateConfirm}
          matters={matters}
          projects={[project]}
          defaultMatterId={editingTemplate.matterId}
          defaultProjectId={project.id}
        />
      )}
    </div>
  );
}